// signal: the flair layer on top of station changes. watches the page, never drives it.
//  - a glow layer under the page (tinted by body[data-station], styled in signal.css)
//  - the station scenes on the ambient canvas (ambient_scenes.ts)
//  - when a page opens, its title scrambles and locks in left to right while the channel number tunes from the old
//    frequency to the new, and a small dial needle swings to the station
//  - a one time power-on per session
// everything stands down for calm motion, reduced motion, sparkle effects off or "station worlds" off

import { reducedMotion } from "./motion_pref";
import { startScenes } from "./ambient_scenes";

const ORDER = ["home", "plan", "studio", "practice", "money", "tools", "core"];
const TITLES = ".view-head h1, .hero h1, .refs-bar h1";
const LOCK_MS = 360;
const STEP_MS = 34;
const GLYPHS = "#@%&0123456789?*+=<>/";
const KANA = "アイウエオカキクケコサシスセソ";

const allowed = (): boolean => {
  const root = document.documentElement;
  return root.dataset.worlds !== "off" && !root.classList.contains("nojuice") && !reducedMotion();
};

// ---------- title lock-in ----------

interface Job {
  title: HTMLElement;
  nodes: Array<{ node: Text; text: string; shown: string }>;
  label: string;
  hadLabel: string | null;
  stamp: string;
  from: number | null;
  to: number | null;
  start: number;
  last: number;
  height: string;
}

let job: Job | null = null;
let raf = 0;
let lastFreq: number | null = null;

const freqOf = (code: string): number | null => {
  const found = /(\d{2,3}\.\d)/.exec(code);
  return found ? Number(found[1]) : null;
};

function glyph(ch: string): string {
  if (/\s/.test(ch)) return ch;
  if (/[^\u0000-ɏ]/.test(ch)) return KANA[Math.floor(Math.random() * KANA.length)];
  if (/[A-Za-z0-9]/.test(ch)) return GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
  return ch;
}

function finish(done: Job | null): void {
  if (!done) return;
  const { title } = done;
  for (const part of done.nodes) {
    // a page that rewrote its title in the meantime keeps what it wrote
    if (part.node.isConnected && part.node.data === part.shown) part.node.data = part.text;
  }
  title.classList.remove("sig-lock");
  title.style.height = done.height;
  title.style.overflow = "";
  if (done.hadLabel === null) title.removeAttribute("aria-label");
  else title.setAttribute("aria-label", done.hadLabel);
  if (done.stamp) title.dataset.code = done.stamp;
}

function frame(now: number): void {
  const current = job;
  if (!current) return;
  if (!current.title.isConnected) {
    job = null;
    return;
  }
  raf = requestAnimationFrame(frame);
  if (now - current.last < STEP_MS) return;
  current.last = now;
  const p = Math.min(1, (now - current.start) / LOCK_MS);
  if (p >= 1) {
    job = null;
    cancelAnimationFrame(raf);
    finish(current);
    return;
  }
  let total = 0;
  for (const part of current.nodes) total += part.text.length;
  // locked letters grow from the left, a little ahead of the clock so the end is crisp
  const locked = Math.floor(Math.min(1, p * 1.15) * total);
  let seen = 0;
  for (const part of current.nodes) {
    if (!part.node.isConnected || part.node.data !== part.shown) {
      // somebody else wrote the title: let go of it
      job = null;
      cancelAnimationFrame(raf);
      part.shown = "";
      finish(current);
      return;
    }
    let out = "";
    for (let i = 0; i < part.text.length; i++) out += seen + i < locked ? part.text[i] : glyph(part.text[i]);
    seen += part.text.length;
    part.shown = out;
    part.node.data = out;
  }
  if (current.from !== null && current.to !== null) {
    const f = current.from + (current.to - current.from) * (1 - Math.pow(1 - p, 3));
    current.title.dataset.code = current.stamp.replace(/\d{2,3}\.\d/, f.toFixed(1));
  }
}

function lock(view: HTMLElement): void {
  if (!allowed()) return;
  const title = [...view.querySelectorAll<HTMLElement>(TITLES)].find((item) => !item.closest(".viewer"));
  if (!title) return;
  cancelAnimationFrame(raf);
  finish(job);
  job = null;

  const stamp = title.dataset.code || "";
  const to = freqOf(stamp);
  const from = lastFreq;
  if (to !== null) lastFreq = to;

  const nodes: Job["nodes"] = [];
  for (const child of title.childNodes) {
    if (child.nodeType === Node.TEXT_NODE && (child as Text).data.trim()) nodes.push({ node: child as Text, text: (child as Text).data, shown: (child as Text).data });
  }
  if (!nodes.length) return;
  const label = nodes.map((part) => part.text).join("").trim();
  const hadLabel = title.getAttribute("aria-label");
  const height = title.style.height;
  // hold the title's height so scrambled letters of other widths never push the page around
  title.style.height = `${title.offsetHeight}px`;
  title.style.overflow = "hidden";
  title.setAttribute("aria-label", label);
  title.classList.add("sig-lock");
  job = { title, nodes, label, hadLabel, stamp, from: from !== null && to !== null && from !== to ? from : null, to, start: performance.now(), last: 0, height };
  raf = requestAnimationFrame(frame);
}

// ---------- live dial ----------

function placeOf(station: string | undefined): number {
  const at = ORDER.indexOf(station || "");
  return at < 0 ? 0 : at / (ORDER.length - 1);
}

let lastPlace: number | null = null;

function dial(view: HTMLElement, animate: boolean): void {
  const title = [...view.querySelectorAll<HTMLElement>(TITLES)].find((item) => !item.closest(".viewer"));
  if (!title) return;
  let strip = title.querySelector<HTMLElement>(":scope > .sig-dial");
  if (!strip) {
    strip = document.createElement("span");
    strip.className = "sig-dial";
    strip.setAttribute("aria-hidden", "true");
    const needle = document.createElement("i");
    needle.className = "sig-needle";
    needle.append(document.createElement("b"));
    strip.append(needle);
    title.append(strip);
  }
  if (!document.body.dataset.station) {
    strip.classList.add("tight");
    return;
  }
  const to = placeOf(document.body.dataset.station);
  strip.style.setProperty("--to", String(to));
  strip.style.setProperty("--from", String(lastPlace ?? to));
  // too narrow to sit beside the stamp without touching it: leave it out
  strip.classList.toggle("tight", title.clientWidth < (title.dataset.code || "").length * 9 + 100);
  strip.classList.remove("go");
  if (animate && lastPlace !== null && lastPlace !== to) {
    void strip.offsetWidth;
    strip.classList.add("go");
  }
  lastPlace = to;
}

// ---------- watching page changes ----------

function watchViews(): void {
  const content = document.getElementById("content");
  if (!content) return;
  const views = new MutationObserver((records) => {
    const opened = new Set<HTMLElement>();
    for (const record of records) {
      const view = record.target as HTMLElement;
      // a view that just gained "entering" is a page opening. the same view re-rendering changes nothing here
      if (view.classList.contains("view") && view.classList.contains("active") && view.classList.contains("entering") && !(record.oldValue || "").split(" ").includes("entering")) opened.add(view);
    }
    for (const view of opened) {
      if (document.documentElement.dataset.worlds === "off") break;
      // this runs after navigation has set the page's code stamp
      dial(view, allowed());
      lock(view);
    }
  });
  // the page sections themselves, not every element inside them
  content.querySelectorAll<HTMLElement>(".view").forEach((view) => views.observe(view, { attributes: true, attributeFilter: ["class"], attributeOldValue: true }));

  // the page the app opened on: a dial that is simply in place
  new MutationObserver(() => {
    const view = content.querySelector<HTMLElement>(".view.active");
    if (view && lastPlace === null && document.documentElement.dataset.worlds !== "off") dial(view, false);
  }).observe(document.body, { attributes: true, attributeFilter: ["data-station"] });
}

// ---------- power on ----------

const BOOT_KEY = "chix-powered";

function powerOn(): void {
  if (!allowed()) return;
  try {
    if (sessionStorage.getItem(BOOT_KEY)) return;
    sessionStorage.setItem(BOOT_KEY, "1");
  } catch {
    return;
  }
  const screen = document.createElement("div");
  screen.className = "sig-boot";
  screen.setAttribute("aria-hidden", "true");
  const mark = document.createElement("b");
  mark.textContent = "CHIX";
  screen.append(document.createElement("i"), mark);
  document.body.append(screen);
  let gone = false;
  const remove = () => {
    if (gone) return;
    gone = true;
    screen.remove();
    window.removeEventListener("pointerdown", skip, true);
    window.removeEventListener("keydown", skip, true);
  };
  const skip = () => {
    screen.classList.add("skip");
    window.setTimeout(remove, 160);
  };
  window.addEventListener("pointerdown", skip, { capture: true, passive: true, once: true });
  window.addEventListener("keydown", skip, { capture: true, passive: true, once: true });
  screen.addEventListener("animationend", (event) => {
    if (event.target === screen) remove();
  });
  window.setTimeout(remove, 1600);
}

export function setupSignal(): void {
  const glow = document.createElement("div");
  glow.className = "station-glow";
  glow.setAttribute("aria-hidden", "true");
  const canvas = document.querySelector("canvas.ambient");
  if (canvas) canvas.after(glow);
  else document.body.prepend(glow);
  startScenes();
  watchViews();
  powerOn();
}
